import { useCallback } from "react";
import {
  type Location,
  type To,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { useBackgroundLocation } from "../home/components/UseBackgroundLocation";
import { MODAL_LOCATION_TYPE, type ModalLocation } from "../routes/AppRoutes";

function isModalLocation(to: To | ModalLocation): to is ModalLocation {
  return (
    typeof to !== "string" && "type" in to && to.type === MODAL_LOCATION_TYPE
  );
}

export const useAppNavigate = <
  TState extends {},
  TStateWithBackground = TState & { background?: Location },
>(): {
  push: (to: To | ModalLocation, state?: Location<TState>["state"]) => void;
  replace: (to: To | ModalLocation, state?: Location<TState>["state"]) => void;
  pop: () => void;
  locationState: Location<TStateWithBackground>["state"];
  removeLocationState: (key: keyof TState) => void;
} => {
  const navigate = useNavigate();
  const location = useLocation();
  const backgroundLocation = useBackgroundLocation();

  const push = useCallback(
    (to: To | ModalLocation, state?: Location<TState>["state"]) => {
      if (isModalLocation(to)) {
        navigate(to.path, {
          state: {
            ...location.state,
            ...state,
            background: backgroundLocation,
          },
        });
      } else {
        navigate(to, { state });
      }
    },
    [navigate, location.state, backgroundLocation],
  );

  const replace = useCallback(
    (to: To | ModalLocation, state?: Location<TState>["state"]) => {
      if (isModalLocation(to)) {
        navigate(to.path, {
          state: {
            ...location.state,
            ...state,
            background: backgroundLocation,
          },
          replace: true,
        });
      } else {
        navigate(to, { replace: true, state });
      }
    },
    [backgroundLocation, location.state, navigate],
  );

  const pop = useCallback(() => {
    if (location.state?.background) {
      navigate(location.state?.background, { replace: true });
    } else {
      navigate(-1);
    }
  }, [location, navigate]);

  const removeLocationState = useCallback(
    (key: keyof TState) => {
      const { [key]: _, ...newState } = location.state ?? ({} as TState);
      navigate(location.pathname, { state: newState });
    },
    [location.pathname, location.state, navigate],
  );

  return {
    push,
    replace,
    pop,
    locationState: location.state,
    removeLocationState,
  };
};
